import globalStore from "@/lib/store";
import store from "@/lib/store";
import { API_URL } from "@/config";
import axios from "axios";
import { getDaysDiff } from "@/utils/date";
import { isBalancePerChildrenEnabled } from "@/modules/settings/queries/tenantSettingsSelectors";

let balanceRefreshTimer = null;
let balanceRefreshRunAt = 0;

export default {
  namespaced: true,

  state: {
    currentUserBalance: JSON.parse(localStorage.getItem("currentUserBalance")) || {
      amount: 0.0,
      currency: "PLN",
    },
    currentUserActiveOrdersAmount: JSON.parse(
      localStorage.getItem("currentUserActiveOrdersAmount"),
    ) || {
      amount: 0.0,
      currency: "PLN",
    },
    negativeBalanceInfoLastShownAt: localStorage.getItem("negativeBalanceInfoLastShownAt") || null,
  },

  getters: {
    hasNegativeBalance: (state, getters, rootState, rootGetters) => {
      if (!rootGetters["auth/isCustomer"]) {
        return false;
      }

      return state.currentUserBalance.amount < 0;
    },

    shouldShowNegativeBalanceInfo: (state, getters) => {
      if (!getters.hasNegativeBalance) {
        return false;
      }

      return (
        !state.negativeBalanceInfoLastShownAt ||
        getDaysDiff(state.negativeBalanceInfoLastShownAt) >= 10
      );
    },
  },

  mutations: {
    setCurrentUserBalance(state, currentUserBalance) {
      localStorage.setItem("currentUserBalance", JSON.stringify(currentUserBalance));

      state.currentUserBalance = currentUserBalance;
    },

    revokeCurrentUserBalance(state) {
      localStorage.removeItem("currentUserBalance");

      state.currentUserBalance = { amount: 0.0, currency: "PLN" };
    },

    setCurrentUserActiveOrdersAmount(state, currentUserActiveOrdersAmount) {
      localStorage.setItem(
        "currentUserActiveOrdersAmount",
        JSON.stringify(currentUserActiveOrdersAmount),
      );

      state.currentUserActiveOrdersAmount = currentUserActiveOrdersAmount;
    },

    revokeCurrentUserActiveOrdersAmount(state) {
      localStorage.removeItem("currentUserActiveOrdersAmount");

      state.currentUserActiveOrdersAmount = { amount: 0.0, currency: "PLN" };
    },

    setNegativeBalanceInfoLastShownAt(state, negativeBalanceInfoLastShownAt) {
      localStorage.setItem("negativeBalanceInfoLastShownAt", negativeBalanceInfoLastShownAt);

      state.negativeBalanceInfoLastShownAt = negativeBalanceInfoLastShownAt;
    },

    revokeNegativeBalanceInfoLastShownAt(state) {
      localStorage.removeItem("negativeBalanceInfoLastShownAt");

      state.negativeBalanceInfoLastShownAt = null;
    },

    // Mirrors revokeCurrentUserBalance / revokeCurrentUserActiveOrdersAmount /
    // revokeNegativeBalanceInfoLastShownAt - Vuex mutations can't call other mutations,
    // so the localStorage cleanup has to be repeated here in sync with them.
    resetState(state) {
      localStorage.removeItem("currentUserBalance");
      localStorage.removeItem("currentUserActiveOrdersAmount");
      localStorage.removeItem("negativeBalanceInfoLastShownAt");

      state.currentUserBalance = { amount: 0.0, currency: "PLN" };
      state.currentUserActiveOrdersAmount = { amount: 0.0, currency: "PLN" };
      state.negativeBalanceInfoLastShownAt = null;
    },
  },

  actions: {
    unsetCurrentUserBalance({ commit }) {
      commit("revokeCurrentUserBalance");
    },

    async updateCurrentUserBalance() {
      if (!globalStore.getters["auth/isAuthenticated"]) {
        return;
      }

      const currentUserBalance = await globalStore.dispatch(
        "transactions/getUserBalance",
        globalStore.getters["auth/user"].id,
      );

      globalStore.commit("users/setCurrentUserBalance", currentUserBalance);
    },

    async bulkPaymentsOverdue(_, formData) {
      const res = await axios.post(`${API_URL}/users/bulkPaymentsOverdue`, formData);

      if (res.status !== 200 || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async selfRemoveAccount() {
      const response = await axios.post(`${API_URL}/users/account/remove`);

      if (response.status !== 200 || !response.data) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    // TODO - to jest raczej zbędne od kiedy pojawiło się selfRemoveAccount()
    // async sendDeleteAccountRequestEmail({commit}, {userId, userType, formData}) {
    //     const response = await axios.post(`${API_URL}/users/${userType}/${userId}/account/sendDeleteAccountRequestMail`, formData);
    //
    //     if (response.status !== 200 || !response.data) {
    //         throw new Error('Error occurred while processing your request.');
    //     }
    //
    //     return response.data;
    // },

    async findUser(_, userId) {
      const response = await axios.get(`${API_URL}/users/${userId}`);

      if (response.status !== 200 || !response.data) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    unsetCurrentUserActiveOrdersAmount({ commit }) {
      commit("revokeCurrentUserActiveOrdersAmount");
    },

    async updateCurrentUserActiveOrdersAmount() {
      if (!globalStore.getters["auth/isAuthenticated"]) {
        return;
      }

      const ordersTotalAmounts = await globalStore.dispatch("orders/getOrdersTotalAmounts", {
        userId: store.getters["auth/user"].id,
        status: "active",
      });

      globalStore.commit(
        "users/setCurrentUserActiveOrdersAmount",
        ordersTotalAmounts.totalGrossAmount,
      );
    },

    async getUsersOptions(_, criteria = {}) {
      const res = await axios.get(`${API_URL}/users/options`, {
        params: criteria,
      });

      if (!res.data || typeof res.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getUsersActivity(_, criteria = {}) {
      const res = await axios.get(`${API_URL}/users/activity`, {
        params: criteria,
      });

      if (!res.data || typeof res.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    updateCurrentUserOrChildBalanceAfterWhile({ dispatch }, timeout = 2000) {
      const runAt = Date.now() + timeout;

      if (balanceRefreshTimer !== null) {
        if (runAt >= balanceRefreshRunAt) {
          return;
        }

        clearTimeout(balanceRefreshTimer);
      }

      balanceRefreshRunAt = runAt;

      balanceRefreshTimer = setTimeout(() => {
        balanceRefreshTimer = null;

        if (!globalStore.getters["auth/isAuthenticated"]) {
          return;
        }

        dispatch("users/updateCurrentUserBalance", null, { root: true });
        dispatch("users/updateCurrentUserActiveOrdersAmount", null, { root: true });

        if (isBalancePerChildrenEnabled.value) {
          dispatch("children/updateCurrentChildBalance", null, { root: true });
        }
      }, timeout);
    },
  },
};
